/**
 * A preview of an MPEG transport stream (.ts) that the browser cannot play: its length, picture size, a set of still
 * frames spread over the video, and whether it carries flight data (MISB 0601 KLV with a position that moves or a
 * height). Only slices of the file are read, so a 10 GB file previews as fast as a small one. Frames are keyframes
 * (they decode on their own) taken near evenly spaced byte offsets and decoded with WebCodecs (H.264 and HEVC).
 */

const PACKET = 188;
const PTS_HZ = 90000;
const PTS_WRAP = 2 ** 33;
const STREAM_H264 = 0x1b;
const STREAM_HEVC = 0x24;
const STREAM_METADATA = 0x15;
const STREAM_PRIVATE = 0x06;
const KLV_KEY = [0x06, 0x0e, 0x2b, 0x34, 0x02, 0x0b, 0x01, 0x01, 0x0e, 0x01, 0x03, 0x01, 0x01];
const CHUNK = 4 * 1024 * 1024;
const MAX_SCAN = 48 * 1024 * 1024;

const readBytes = async (file, start, end) =>
	new Uint8Array(await file.slice(Math.max(0, start), Math.min(file.size, end)).arrayBuffer());

/** The first offset in `buf` where packets line up on the sync byte for several packets in a row. */
export const syncOffset = buf => {
	for (let i = 0; i < Math.min(buf.length, PACKET * 2); i += 1) {
		let ok = buf[i] === 0x47;
		for (let k = 1; k < 4 && ok && i + k * PACKET < buf.length; k += 1)
			ok = buf[i + k * PACKET] === 0x47;
		if (ok) return i;
	}
	return -1;
};

/** Each transport packet's PID, start-of-unit flag and payload. */
export const packets = function* packets(buf) {
	const s = syncOffset(buf);
	if (s < 0) return;
	for (let i = s; i + PACKET <= buf.length; i += PACKET) {
		if (buf[i] !== 0x47) continue;
		const pid = ((buf[i + 1] & 0x1f) << 8) | buf[i + 2];
		const start = (buf[i + 1] & 0x40) !== 0;
		const afc = (buf[i + 3] >> 4) & 3;
		let p = i + 4;
		if (afc === 2) continue;
		if (afc === 3) p += 1 + buf[i + 4];
		if (p >= i + PACKET) continue;
		yield { pid, start, payload: buf.subarray(p, i + PACKET) };
	}
};

/** The 33-bit PTS of a PES header, or null. */
export const pesPts = pes => {
	if (pes.length < 14 || pes[0] !== 0 || pes[1] !== 0 || pes[2] !== 1) return null;
	if (!(pes[7] & 0x80)) return null;
	const b = pes.subarray(9, 14);
	return (
		(b[0] & 0x0e) * 2 ** 29 +
		b[1] * 2 ** 22 +
		(b[2] & 0xfe) * 2 ** 14 +
		b[3] * 2 ** 7 +
		(b[4] >> 1)
	);
};

const pesPayload = pes => pes.subarray(9 + pes[8]);

/** Video and flight-data PIDs from the program map. */
export const streamMap = buf => {
	let pmtPid = null;
	for (const { pid, start, payload } of packets(buf)) {
		if (pid === 0 && start && pmtPid == null) {
			const t = payload.subarray(1 + payload[0]);
			if (t.length >= 12) pmtPid = ((t[10] & 0x1f) << 8) | t[11];
		} else if (pmtPid != null && pid === pmtPid && start) {
			const t = payload.subarray(1 + payload[0]);
			const sectionEnd = Math.min(t.length, 3 + (((t[1] & 0x0f) << 8) | t[2]) - 4);
			let p = 12 + (((t[10] & 0x0f) << 8) | t[11]);
			const out = { video: null, codec: null, klv: null };
			while (p + 5 <= sectionEnd) {
				const type = t[p];
				const es = ((t[p + 1] & 0x1f) << 8) | t[p + 2];
				const infoLen = ((t[p + 3] & 0x0f) << 8) | t[p + 4];
				const info = t.subarray(p + 5, p + 5 + infoLen);
				if ((type === STREAM_H264 || type === STREAM_HEVC) && out.video == null) {
					out.video = es;
					out.codec = type === STREAM_HEVC ? 'hevc' : 'h264';
				}
				const klvTag = String.fromCharCode(...info).includes('KLVA');
				if (type === STREAM_METADATA || (type === STREAM_PRIVATE && klvTag)) out.klv = es;
				p += 5 + infoLen;
			}
			return out;
		}
	}
	return null;
};

/** Complete PES packets of one PID in a buffer (the unit still open at the end is dropped). */
export const pesUnits = (buf, wantPid) => {
	const units = [];
	let parts = null;
	for (const { pid, start, payload } of packets(buf)) {
		if (pid !== wantPid) continue;
		if (start) {
			if (parts) units.push(concat(parts));
			parts = [payload];
		} else if (parts) parts.push(payload);
	}
	return units;
};

const concat = parts => {
	const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
	let o = 0;
	for (const p of parts) {
		out.set(p, o);
		o += p.length;
	}
	return out;
};

/** NAL unit types in an Annex B access unit. */
export const nalTypes = (au, codec) => {
	const types = [];
	for (let i = 0; i + 3 < au.length; i += 1) {
		if (au[i] === 0 && au[i + 1] === 0 && au[i + 2] === 1) {
			const h = au[i + 3];
			types.push(codec === 'hevc' ? (h >> 1) & 0x3f : h & 0x1f);
			i += 2;
		}
	}
	return types;
};

const isKeyNal = (t, codec) => (codec === 'hevc' ? t >= 16 && t <= 21 : t === 5);
const hasParams = (types, codec) =>
	codec === 'hevc'
		? types.includes(33) && types.includes(34)
		: types.includes(7) && types.includes(8);

/** A keyframe that carries its own parameter sets, so it decodes without anything before it. */
export const isSelfContainedKey = (au, codec) => {
	const t = nalTypes(au, codec);
	return t.some(x => isKeyNal(x, codec)) && hasParams(t, codec);
};

/**
 * The first keyframe in a run of PES units, with the parameter sets it needs. Some recorders (DJI) put them in a
 * PES of their own just before the keyframe; `params` is the last set seen earlier in the file, used when the
 * buffer starts between the two. Returns { au, unit, params } or { params } when there is no keyframe.
 */
export const firstKeyframe = (units, codec, params = null) => {
	let last = params;
	for (const u of units) {
		const au = pesPayload(u);
		const types = nalTypes(au, codec);
		const own = hasParams(types, codec);
		if (types.some(x => isKeyNal(x, codec))) {
			if (own) return { au, unit: u, params: au };
			if (last) return { au: concat([last, au]), unit: u, params: last };
		} else if (own) last = au;
	}
	return { params: last };
};

/** WebCodecs codec string from an H.264 SPS; HEVC uses Main (or Main 10) at level 5.1, which decoders accept. */
const codecStrings = (au, codec) => {
	if (codec === 'hevc') return ['hvc1.1.6.L153.B0', 'hvc1.2.4.L153.B0', 'hev1.1.6.L153.B0'];
	for (let i = 0; i + 7 < au.length; i += 1)
		if (au[i] === 0 && au[i + 1] === 0 && au[i + 2] === 1 && (au[i + 3] & 0x1f) === 7) {
			const hex = b => b.toString(16).padStart(2, '0');
			return [`avc1.${hex(au[i + 4])}${hex(au[i + 5])}${hex(au[i + 6])}`, 'avc1.640033'];
		}
	return ['avc1.640033'];
};

// ---- flight data -------------------------------------------------------------------------------------------------

const berLength = (b, i) => {
	const n = b[i];
	if (n < 0x80) return [n, i + 1];
	let v = 0;
	for (let k = 1; k <= (n & 0x7f); k += 1) v = v * 256 + b[i + k];
	return [v, i + 1 + (n & 0x7f)];
};

const int = (b, signed) => {
	let v = 0;
	for (const x of b) v = v * 256 + x;
	return signed && b.length && b[0] & 0x80 ? v - 2 ** (8 * b.length) : v;
};

/** Position and height from one MISB 0601 local set (lat/lon tags 13/14; height from tags 15, 75 or 78). */
export const parseKlv = buf => {
	for (let i = 0; i + 16 < buf.length; i += 1) {
		if (!KLV_KEY.every((v, k) => buf[i + k] === v)) continue;
		const [len, start] = berLength(buf, i + 16);
		const end = Math.min(buf.length, start + len);
		const out = {};
		let p = start;
		while (p + 2 <= end) {
			const tag = buf[p];
			const [l, v0] = berLength(buf, p + 1);
			const v = buf.subarray(v0, v0 + l);
			if (tag === 13) out.lat = (int(v, true) * 180) / 4294967294;
			else if (tag === 14) out.lon = (int(v, true) * 360) / 4294967294;
			else if (tag === 15) out.alt = (int(v) * 19900) / 65535 - 900;
			else if (tag === 75) out.hae = (int(v) * 19900) / 65535 - 900;
			else if (tag === 78) out.centreHae = (int(v) * 19900) / 65535 - 900;
			p = v0 + l;
		}
		return Number.isFinite(out.lat) && Number.isFinite(out.lon) ? out : null;
	}
	return null;
};

/** Flight data is a position that moves, or a height above the ground; a frozen point without height is not. */
export const flightDataFound = samples => {
	if (samples.length < 2) return false;
	const lat = samples.map(s => s.lat);
	const lon = samples.map(s => s.lon);
	const k = Math.cos((lat[0] * Math.PI) / 180);
	const extent = Math.hypot(
		(Math.max(...lon) - Math.min(...lon)) * 111320 * k,
		(Math.max(...lat) - Math.min(...lat)) * 110574
	);
	const height = samples.some(
		s => Number.isFinite(s.hae) && Number.isFinite(s.centreHae) && s.hae - s.centreHae > 2
	);
	return extent > 5 || height;
};

// ---- the preview --------------------------------------------------------------------------------------------------

// drawn as soon as the decoder hands a frame over: holding decoded 4K frames stalls the decoder's frame pool
const drawFrame = (frame, width) => {
	const w = Math.min(width, frame.displayWidth);
	const canvas = document.createElement('canvas');
	canvas.width = w;
	canvas.height = Math.round((w * frame.displayHeight) / frame.displayWidth);
	canvas.getContext('2d').drawImage(frame, 0, 0, canvas.width, canvas.height);
	return canvas;
};

const canvasToImage = async canvas => {
	const blob = await new Promise(r => canvas.toBlob(r, 'image/jpeg', 0.85));
	return { url: URL.createObjectURL(blob), bitmap: await createImageBitmap(canvas) };
};

/** One keyframe near byte `offset`: reads forward in chunks until a keyframe has been seen whole. */
const keyframeNear = async (file, map, offset) => {
	let from = Math.floor(offset / PACKET) * PACKET;
	const stop = Math.min(file.size, from + MAX_SCAN);
	while (from < stop) {
		const buf = await readBytes(file, from, Math.min(stop, from + CHUNK + PACKET * 4));
		const found = firstKeyframe(pesUnits(buf, map.video), map.codec, map.params);
		map.params = found.params || map.params;
		const klv = map.klv != null ? pesUnits(buf, map.klv).map(u => parseKlv(pesPayload(u))) : [];
		if (found.au) return { au: found.au, pts: pesPts(found.unit), klv: klv.filter(Boolean) };
		from += CHUNK;
	}
	return null;
};

const lastPts = async (file, map) => {
	for (let back = CHUNK; back <= MAX_SCAN; back *= 2) {
		const buf = await readBytes(file, file.size - back, file.size);
		const pts = pesUnits(buf, map.video)
			.map(pesPts)
			.filter(v => v != null);
		if (pts.length) return Math.max(...pts);
	}
	return null;
};

/**
 * { duration, width, height, codec, frames: [{ t, url, bitmap }], flightData: bool } for a .ts File, or null when
 * the browser cannot decode it. onFrame(frames so far) reports progress.
 */
export const previewTransportStream = async (file, { count = 24, width = 960, onFrame } = {}) => {
	if (typeof VideoDecoder === 'undefined') return null;
	const head = await readBytes(file, 0, CHUNK);
	const map = streamMap(head);
	if (!map?.video) return null;
	const first = await keyframeNear(file, map, 0);
	if (!first) return null;
	const end = await lastPts(file, map);
	const span = end != null ? (end - first.pts + PTS_WRAP) % PTS_WRAP : 0;

	let config = null;
	for (const codec of codecStrings(first.au, map.codec)) {
		const { supported } = await VideoDecoder.isConfigSupported({ codec }).catch(() => ({}));
		if (supported) {
			config = { codec };
			break;
		}
	}
	if (!config) return null;

	const keys = [first];
	const flight = [...first.klv];
	for (let k = 1; k < count; k += 1) {
		const key = await keyframeNear(file, map, (file.size * k) / count);
		if (!key || keys.some(x => x.pts === key.pts)) continue;
		keys.push(key);
		flight.push(...key.klv);
	}

	const drawn = new Map();
	let size = null;
	const decoder = new VideoDecoder({
		output: frame => {
			size = size || { width: frame.displayWidth, height: frame.displayHeight };
			drawn.set(frame.timestamp, drawFrame(frame, width));
			frame.close();
		},
		error: () => {},
	});
	decoder.configure(config);
	keys.forEach((key, i) =>
		decoder.decode(new EncodedVideoChunk({ type: 'key', timestamp: i, data: key.au }))
	);
	await decoder.flush().catch(() => {});
	const frames = [];
	for (let i = 0; i < keys.length; i += 1) {
		const canvas = drawn.get(i);
		if (!canvas) continue;
		frames.push({
			t: ((keys[i].pts - first.pts + PTS_WRAP) % PTS_WRAP) / PTS_HZ,
			...(await canvasToImage(canvas)),
		});
		onFrame?.([...frames]);
	}
	decoder.close();
	if (!frames.length || !size) return null;
	return {
		duration: span / PTS_HZ + 1 / 30,
		width: size.width,
		height: size.height,
		codec: map.codec,
		frames,
		flightData: map.klv != null && flightDataFound(flight),
	};
};

/** The preview frame closest to time t (seconds). */
export const frameAt = (frames, t) =>
	frames?.length
		? frames.reduce((best, f) => (Math.abs(f.t - t) < Math.abs(best.t - t) ? f : best))
		: null;
